<template>
  <div class="rd-screen">
    <report-meals-packages-add-meal
      v-if="selectedCompany"
      v-model="addMealDialog"
      :company="selectedCompany"
      @refreshMeal="loadMealsForRestaurantCompanies"
    ></report-meals-packages-add-meal>
    <print-layout-dialog v-model="printLayoutDialog" :branches="hasBranchGroups" @choose="printCompanies" />

    <report-tools>
      <span class="rd-muted">{{ $t("report_desktop.packing_local_note") }}</span>
      <template #right>
        <button
          v-if="hasBranchGroups"
          type="button"
          class="ui-btn ui-btn--sm"
          :class="{ 'ui-btn--primary': sumBranches }"
          :aria-pressed="String(sumBranches)"
          :title="$t('packing_sum_hint')"
          @click="setSumBranches(!sumBranches)"
        >
          <v-icon size="16" :color="sumBranches ? '#ffffff' : undefined">mdi-sigma</v-icon>
          {{ $t("packing_sum_toggle") }}
        </button>
        <button v-if="current" type="button" class="ui-btn ui-btn--sm" @click="printCompany">
          <v-icon size="16">mdi-printer-outline</v-icon>
          {{ $t("report_desktop.print_company") }}
        </button>
      </template>
    </report-tools>

    <report-state
      v-if="!selectedCompanies.length"
      icon="mdi-domain"
      :title="$t('no_companies_selected')"
    ></report-state>
    <report-state v-else-if="failed" kind="error" @retry="loadMealsForRestaurantCompanies"></report-state>
    <report-state v-else-if="isLoading" kind="loading"></report-state>
    <report-state
      v-else-if="!packingCompanies.length"
      icon="mdi-shopping-outline"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <div v-else class="rd-pack">
      <div class="rd-company-list" role="list">
        <button
          v-for="company in packingCompanies"
          :key="company.key"
          type="button"
          class="rd-company"
          role="listitem"
          :aria-pressed="String(current && current.key === company.key)"
          @click="selectedKey = company.key"
        >
          <span class="rd-company__row">
            <span class="rd-company__name" :title="company.name">
              <v-icon v-if="company.isGroup" size="14" :aria-label="$t('packing_sum_toggle')">mdi-sigma</v-icon>
              {{ company.name }}
            </span>
            <span class="rd-company__progress rd-num" :class="{ 'is-complete': company.complete }">
              <v-icon v-if="company.ready" size="16" color="#15803d">mdi-check</v-icon>
              {{ company.done }} / {{ company.total }}
            </span>
          </span>
          <span class="rd-bar" :class="{ 'is-complete': company.complete }">
            <i :style="{ width: `${company.percent}%` }"></i>
          </span>
          <span class="rd-company__sub">
            <span v-for="group in company.groups" :key="group.id" class="rd-company__shift">
              <report-shift-tag :shift="group.id"></report-shift-tag>
              <b class="rd-num" :class="{ 'is-complete': group.complete }">
                <v-icon v-if="group.complete" size="12" color="#15803d">mdi-check</v-icon>{{ group.done }}/{{ group.total }}
              </b>
            </span>
            <span class="rd-company__status">{{ statusText(company) }}</span>
          </span>
        </button>
      </div>

      <div v-if="current" class="rd-detail rd-detail--aside">
        <div class="rd-detail__head">
          <div>
            <h2>{{ current.name }}</h2>
            <span class="rd-detail__sub">{{ detailSubtitle(current) }}</span>
          </div>
          <span class="rd-detail__spacer"></span>
          <span class="rd-status rd-num" :class="current.complete ? 'rd-status--all' : 'rd-status--none'">
            {{ current.done }} / {{ current.total }}
          </span>
        </div>

        <div class="rd-checklist-rows" role="group" :aria-label="current.name" @keydown="onItemsKey">
          <template v-for="group in current.groups">
            <!-- pakuje se i nosi po smeni: svaka smena ima svoj spisak i svoj napredak -->
            <div :key="`shift-${group.id}`" class="rd-check-shift" :class="{ 'is-complete': group.complete }">
              <report-shift-tag :shift="group.id" long></report-shift-tag>
              <i class="rd-check-shift__rule"></i>
              <span class="rd-num rd-check-shift__progress">
                <v-icon v-if="group.complete" size="14" color="#15803d">mdi-check</v-icon>
                {{ group.done }} / {{ group.total }}
              </span>
            </div>
            <button
              v-for="item in group.items"
              :key="item.key"
              type="button"
              class="rd-check"
              role="checkbox"
              :aria-checked="String(item.packed)"
              @click="togglePacked(current.key, item.key)"
            >
              <span class="rd-check__box" :class="{ 'is-on': item.packed }">
                <v-icon size="16">mdi-check</v-icon>
              </span>
              <span class="rd-check__name" :class="{ 'is-done': item.packed }">
                {{ item.name }}
                <span v-for="badge in item.badges" :key="badge" class="rd-check__tag">{{ badge }}</span>
                <span class="rd-visually-hidden">{{ $t("report_mobile.shift_n", { n: item.shiftId }) }}</span>
              </span>
              <span class="rd-check__qty" :class="{ 'is-done': item.packed }">{{ item.count }}</span>
            </button>
          </template>
        </div>

        <div class="rd-actions">
          <button v-if="isSingleDay && !current.isGroup" type="button" class="ui-btn" @click="openAddDialog(current.source)">
            <v-icon size="16">mdi-plus</v-icon>
            {{ $t("report_mobile.add_meal") }}
          </button>
          <span class="rd-actions__spacer"></span>
          <button v-if="current.ready" type="button" class="ui-btn ui-btn--ok" @click="setReady(current.key, false)">
            <v-icon size="16" color="#ffffff">mdi-check</v-icon>
            {{ $t("report_mobile.company_ready") }}
          </button>
          <button
            v-else-if="current.complete"
            type="button"
            class="ui-btn ui-btn--primary"
            @click="setReady(current.key, true)"
          >
            {{ $t("report_mobile.mark_company_ready") }}
          </button>
          <button v-else type="button" class="ui-btn" disabled>
            {{ $t("report_mobile.pack_remaining", { n: current.total - current.done }) }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import reportService from "@/services/report";
import ReportMealsTabPackages from "@/components/report/meals/tabs/ReportMealsTabPackages.vue";
import hasPlural from "../../shared/hasPlural";
import hasPackingChecklist from "../../shared/hasPackingChecklist";
import hasShiftBinding from "../../shared/hasShiftBinding";
import hasShiftSubtitle from "../../shared/hasShiftSubtitle";
import ReportShiftTag from "../../shared/ReportShiftTag.vue";
import ReportState from "../ReportState.vue";
import ReportTools from "../ReportTools.vue";

/**
 * Pakovanje na računaru: firme levo (napredak po smenama, status), lista za pakovanje izabrane firme desno
 * razvrstana po smenama (pakuje se i nosi po smeni),
 * "Označi firmu kao spremnu" i štampa liste jedne firme. Nasleđuje učitavanje, dodavanje obroka,
 * štampu i Excel iz ReportMealsTabPackages; "spakovano" se čuva samo u ovom pregledaču.
 */
export default {
  name: "ReportDesktopPackages",
  extends: ReportMealsTabPackages,
  mixins: [hasPlural, hasShiftBinding, hasShiftSubtitle, hasPackingChecklist],
  components: { ReportShiftTag, ReportState, ReportTools },
  data: () => ({
    failed: false,
    /** null = automatski (prva firma koja nije spremna) */
    selectedKey: null,
    requestId: 0,
  }),
  computed: {
    current() {
      const list = this.packingCompanies;
      return (
        list.find((company) => company.key === this.selectedKey) ||
        list.find((company) => !company.ready) ||
        list[0] ||
        null
      );
    },
    emptyTitle() {
      return this.filters.search
        ? this.$t("report_mobile.no_results_for", { search: this.filters.search })
        : this.$t("report_mobile.no_meals");
    },
    emptyText() {
      return this.filters.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      const total = this.packingCompanies.reduce((sum, company) => sum + company.total, 0);
      const done = this.packingCompanies.reduce((sum, company) => sum + company.done, 0);
      const ready = this.packingCompanies.filter((company) => company.ready).length;
      return [
        {
          key: "packed",
          label: this.$t("report_desktop.packed"),
          value: done,
          of: total,
          percent: total ? Math.round((done / total) * 100) : 0,
          tone: "inverse",
        },
        {
          key: "ready",
          label: this.$t("report_desktop.companies_ready"),
          value: ready,
          of: this.packingCompanies.length,
        },
        {
          key: "left",
          label: this.$t("report_desktop.remaining"),
          value: total - done,
          tone: total - done ? "accent" : "",
        },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.packingCompanies.length ? kpis : []);
      },
      immediate: true,
    },
    isLoading(loading) {
      this.$emit("loading", loading);
    },
  },
  methods: {
    onPackingStateReset() {
      this.selectedKey = null;
    },
    /** Posle "spremna" prelazi na sledeću firmu koja nije spremna i nudi poništavanje. */
    onCompanyReady(companyKey) {
      const list = this.packingCompanies;
      const start = list.findIndex((company) => company.key === companyKey);
      const next = [...list.slice(start + 1), ...list.slice(0, start)].find((company) => !company.ready);
      this.selectedKey = next ? next.key : companyKey;
      this.$emit("toast", this.$t("report_desktop.company_marked_ready"), {
        label: this.$t("report_desktop.undo"),
        action: () => {
          this.setReady(companyKey, false);
          this.selectedKey = companyKey;
        },
      });
    },
    /** Isto kao stari tab, uz stanje greške i zaštitu od starih odgovora. */
    async loadMealsForRestaurantCompanies() {
      const requestId = ++this.requestId;
      this.failed = false;
      if (!this.selectedCompanies || this.selectedCompanies.length === 0) {
        this.report = [];
        return;
      }
      this.isLoading = true;
      const params = {
        date: this.filters.dates.from,
        date2: this.filters.dates.to,
        company: this.selectedCompanies.map((company) => company.id).join(","),
      };
      try {
        const report = await reportService.getPackagesForRestaurantCompanies(
          this.$store.state.restaurant.id,
          params
        );
        if (requestId === this.requestId) {
          this.report = report;
        }
      } catch (error) {
        if (requestId === this.requestId) {
          this.report = [];
          this.failed = true;
          this.showErrorSnackbar(error, this.$t("error_loading_report"));
        }
      } finally {
        if (requestId === this.requestId) {
          this.isLoading = false;
        }
      }
    },
    detailSubtitle(company) {
      const meals = this.pluralT("report_mobile.meals_n", company.total);
      if (!company.shiftIds.length) {
        return meals;
      }
      const shifts =
        company.shiftIds.length === 1
          ? this.$t("report_mobile.shift_n", { n: company.shiftIds[0] })
          : this.$t("report_mobile.shifts_list", { list: company.shiftIds.join(", ") });
      const base = `${shifts} · ${meals}`;
      return company.isGroup ? `${base} · ${this.$t("packing_sum_members", { list: company.members.join(", ") })}` : base;
    },
    /** Status firme uz oznake smena u listi: nije počelo / u toku / spakovano / spremna. */
    statusText(company) {
      let status = "report_mobile.status_in_progress";
      if (company.ready) {
        status = "report_mobile.status_ready";
      } else if (company.complete) {
        status = "report_desktop.status_packed";
      } else if (company.done === 0) {
        status = "report_mobile.status_not_started";
      }
      return this.$t(status);
    },
    /** Štampa listu samo izabrane firme (isti oblik kao štampa celog Pakovanja). */
    printCompany() {
      const [data] = this.prepareDataForExport([this.current.source]);
      this.printDocument({
        tables: [
          {
            ...data,
            headers: this.headers,
            title: `${data.title} - ${this.subtitle}`,
            subtitle: [this.$t("report"), this.$t("pak")].join(": "),
          },
        ],
        hideDatetime: true,
      });
    },
    /** Strelice gore/dole prelaze između stavki liste. */
    onItemsKey(event) {
      if (!["ArrowDown", "ArrowUp"].includes(event.key)) {
        return;
      }
      const rows = [...event.currentTarget.querySelectorAll(".rd-check")];
      const next = rows[rows.indexOf(document.activeElement) + (event.key === "ArrowDown" ? 1 : -1)];
      if (next) {
        event.preventDefault();
        next.focus();
      }
    },
  },
};
</script>
